import { runInAction } from 'mobx'

import {
  IGeoJson2DGeographicCoordinates,
  IGlobeViewSpecificItemData,
  LocationType,
} from '~/client/graph/types.generated'
import { liveCoordinates } from '~/client/src/shared/utils/liveEquipmentPosition'

import GlobeView from '../../../models/GlobeView'
import LocationBase from '../../../models/LocationObjects/LocationBase'
import OffloadingEquipment from '../../../models/LocationObjects/OffloadingEquipment'
import ProjectLocation from '../../../models/LocationObjects/ProjectLocation'
import SitemapItem from '../../../models/SitemapItem'
import { canRenderWithoutDataObject } from './datalessItemRule'
import MapViewItemBase from './MapViewItemBase'
import MapViewItemFactory from './MapViewItemFactory'

interface IBuildGlobeViewItemsInput {
  globe: GlobeView
  getSitemapItem: (sitemapItemId: string) => SitemapItem | undefined
  // The app reads its hierarchy map; JHP reads its hydrated collections.
  getDataObject: (assignedId: string) => LocationBase | undefined
  // For a ProjectLocation linked to the address; absent keeps its stored coordinates.
  projectCenter?: { lat: number; lng: number }
}

// Mirrors MapViewItemBase's own id and isVisibleOnMap, without constructing one to ask.
interface IResolvedGlobeItem {
  sitemapItem: SitemapItem
  dataObject: LocationBase | undefined
  id: string
  isVisibleOnMap: boolean
}

function resolveGlobeItem(
  globeItem: IGlobeViewSpecificItemData,
  displayDataMap: GlobeView['displayDataMap'],
  getSitemapItem: IBuildGlobeViewItemsInput['getSitemapItem'],
  getDataObject: IBuildGlobeViewItemsInput['getDataObject'],
): IResolvedGlobeItem | null {
  const sitemapItem = getSitemapItem(globeItem.sitemapItemId)
  if (!sitemapItem) {
    return null
  }

  const dataObject = getDataObject(sitemapItem.assignedId)
  if (!dataObject && !canRenderWithoutDataObject(sitemapItem)) {
    return null
  }

  const isDisplayed =
    GlobeView.isItemDisplayedIn(displayDataMap, sitemapItem.id) &&
    !!sitemapItem.coordinates

  return {
    sitemapItem,
    dataObject,
    id: dataObject?.id ?? sitemapItem.id,
    isVisibleOnMap: !!isDisplayed && dataObject?.showInMap !== false,
  }
}

export interface IVisibleGlobeItem {
  id: string
  // Where the full build draws it, so a distance measured to it matches the map.
  coordinates: IGeoJson2DGeographicCoordinates
}

// What a globe places and shows, without the full build's model-per-item cost.
export function globeVisibleItems({
  globe,
  getSitemapItem,
  getDataObject,
  projectCenter,
}: IBuildGlobeViewItemsInput): IVisibleGlobeItem[] {
  const items: IVisibleGlobeItem[] = []
  const displayDataMap = globe?.displayDataMap

  for (const globeItem of globe?.items ?? []) {
    const resolved = resolveGlobeItem(
      globeItem,
      displayDataMap,
      getSitemapItem,
      getDataObject,
    )
    if (resolved?.isVisibleOnMap) {
      items.push({
        id: resolved.id,
        coordinates: isAtProjectCenter(resolved.dataObject, projectCenter)
          ? { latitude: projectCenter.lat, longitude: projectCenter.lng }
          : resolved.sitemapItem.coordinates,
      })
    }
  }

  return items
}

// What a globe draws, without the full build's model-per-item cost. Equality with it is tested.
export function globeDisplayedLocationIds(
  input: Omit<IBuildGlobeViewItemsInput, 'projectCenter'>,
): Set<string> {
  return new Set(globeVisibleItems(input).map(i => i.id))
}

// One builder for every viewer, so a rule about which items exist cannot hold on one map only.
export function buildGlobeViewItems({
  globe,
  getSitemapItem,
  getDataObject,
  projectCenter,
}: IBuildGlobeViewItemsInput): MapViewItemBase[] {
  const items: MapViewItemBase[] = []
  const displayDataMap = globe?.displayDataMap

  for (const globeItem of globe?.items ?? []) {
    const resolved = resolveGlobeItem(
      globeItem,
      displayDataMap,
      getSitemapItem,
      getDataObject,
    )
    if (!resolved) {
      continue
    }

    const { sitemapItem, dataObject } = resolved

    if (!dataObject) {
      items.push(
        MapViewItemFactory.fromGlobeItem(null, sitemapItem.copy(), globe),
      )
      continue
    }

    const item = MapViewItemFactory.fromGlobeItem(
      dataObject.copy(),
      sitemapItem.copy(),
      globe,
    )

    if (isAtProjectCenter(dataObject, projectCenter)) {
      moveToProjectCenter(item, projectCenter)
    }

    if (dataObject.is(LocationType.OffloadingEquipment)) {
      applyEffectivePosition(item, dataObject as OffloadingEquipment)
    }

    items.push(item)
  }

  return items
}

function isAtProjectCenter(
  dataObject: LocationBase | undefined,
  projectCenter: IBuildGlobeViewItemsInput['projectCenter'],
): boolean {
  return (
    !!projectCenter &&
    !!dataObject?.is(LocationType.ProjectLocation) &&
    (dataObject as ProjectLocation).isLinked
  )
}

function moveToProjectCenter(
  item: MapViewItemBase,
  center: { lat: number; lng: number },
): void {
  const { coordinates, shapeCoordinates } = item.sitemapItem
  // A snapshot item can hydrate without coordinates, and this runs inside a computed: a throw here
  // blanks the whole federated map.
  if (!coordinates) {
    return
  }
  const latDif = coordinates.latitude - center.lat
  const lngDif = coordinates.longitude - center.lng

  runInAction(() => {
    shapeCoordinates?.translate(-latDif, -lngDif)
    item.sitemapItem.coordinates = {
      latitude: center.lat,
      longitude: center.lng,
    }
  })
}

// Live-positioned equipment draws where it is now, not where it was placed.
function applyEffectivePosition(
  item: MapViewItemBase,
  equipment: OffloadingEquipment,
): void {
  const live = liveCoordinates(equipment)
  if (!live) {
    return
  }

  runInAction(() => {
    item.sitemapItem.coordinates = live
  })
}
